import { FC, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"

import Status, { StatusType } from "components/Status/Status"
import Text from "components/Text/Text"
import ReservationArticle from "models/ReservationArticle"
import { Guid } from "@/types"
import { Purpose } from "config/constants"
import useAssetInspections from "hooks/useAssetInspections"
import ReservationSidebarConfigurationItem from "./ReservationSidebarConfigurationItem"
import { Flex, FlexItem } from "components/Flex"
import Button from "components/Button/Button"
import Routes from "@/routes"
import { ReservationStep } from "pages/ReservationPage/config"
import { IGetReservationReferenceNumbers } from "models/Reservation"
import HelpButton from "components/HelpButton/HelpButton"
import NoValue from "components/NoValue/NoValue"

interface IReservationSidebarConfigurationProps {
    reservationId: Guid
    configurationCompleted: boolean
    articles: ReservationArticle[]
    nodeArticleId: Guid
    purpose: Purpose
    canEdit?: boolean
    referenceNumbers: IGetReservationReferenceNumbers
}

const ReservationSidebarConfiguration: FC<IReservationSidebarConfigurationProps> = ({
    reservationId,
    configurationCompleted,
    articles,
    nodeArticleId,
    purpose,
    canEdit,
    referenceNumbers,
}) => {
    const { t } = useTranslation()
    const location = useLocation()

    const {
        assetInspections,
        assetInspectionsGrouped,
        getLatestInspections,
        fetchAssetInspections,
    } = useAssetInspections({
        reservationId,
        groupBy: "assetId"
    })

    useEffect(() => {
        fetchAssetInspections()
    }, [fetchAssetInspections])

    const inspectedAssetIds = useMemo(() => {
        if (!assetInspections) return []
        return assetInspections.map(assetInspection => assetInspection.assetId)
    }, [assetInspections])

    const damagedAssetIds = useMemo(() => {
        if (!assetInspectionsGrouped) return []
        const damagedAssets: Guid[] = []
        for (const [assetId, inspections] of assetInspectionsGrouped) {
            const latestInspections = getLatestInspections(inspections)
            if (latestInspections.latestInspection?.damages) {
                damagedAssets.push(assetId as Guid)
            }
        }
        return damagedAssets
    }, [assetInspectionsGrouped, getLatestInspections])

    return (
        <>
            <div className="mb-4">
                <Status type={configurationCompleted ? StatusType.success : StatusType.warning}>
                    <Flex alignItems="center" justifyContent="space-between" gap="1.6rem">
                        <Text>{t(configurationCompleted ? 'requestDetails.configurationComplete' : 'requestDetails.configurationIncomplete')}</Text>
                        {canEdit &&
                            <Button
                                theme="link"
                                type="link"
                                url={Routes.reservationEdit.getPath({ reservationId }, { step: ReservationStep.configuration, backUrl: location.pathname + location.search })}
                                label={t('reservation.editConfiguration')}
                            />
                        }
                    </Flex>
                </Status>
                <Flex className="bg-grey-50 round-sm mt-2 p-2" gap="2.4rem"
                    alignItems="center"
                    justifyContent="space-between">
                    <FlexItem className="word-break-break-word">
                        {t('reservation.costUnit')}: {referenceNumbers.costUnit
                            ? <span className={referenceNumbers.costUnitUserDefined ? undefined : "color-grey-500"}>
                                {referenceNumbers.costUnit}
                            </span>
                            : <NoValue />
                        }
                    </FlexItem>
                    <FlexItem shrink={0}>
                        <HelpButton
                            tooltipProps={{
                                content: <Trans i18nKey={
                                    purpose === Purpose.sale
                                        ? "reservation.costUnitTooltipSale"
                                        : "reservation.costUnitTooltipRent"
                                }
                                    components={{
                                        p: <p />,
                                        ul: <ul />,
                                        li: <li />,
                                        br: <br />,
                                    }}
                                />
                            }}
                        />
                    </FlexItem>
                </Flex>
            </div>
            <div>
                {articles.map((article, idx) => (
                    <div className="reservation-sidebar__configuration-item"
                        key={idx}>
                        <ReservationSidebarConfigurationItem
                            article={article}
                            isNodeArticle={article.articleId === nodeArticleId}
                            purpose={purpose}
                            inspectedAssetIds={inspectedAssetIds}
                            damagedAssetIds={damagedAssetIds}
                        />
                    </div>
                ))}
            </div>
        </>
    )
}

export default ReservationSidebarConfiguration
